// Google reviews for the Kundenstimmen slider ([data-google-reviews="<max>"]). The layout prints the configuration
// (#yn-google: API key, Place ID, language) only when the visitor has agreed to the cookie category "tools", so
// nothing is loaded from Google without consent. The reviews (4 and 5 stars) come from the Places API (New) and
// are kept for 24 h in the framework's server cache ($_yn.cache), so a site asks Google about once a day.
// Every value from Google is inserted as text, never as HTML.
const CACHE_TTL = 86400

const loadMaps = (key, language) =>
	new Promise((resolve, reject) => {
		if (window.google?.maps?.importLibrary) return resolve()
		window.__ynGoogleReady = () => resolve()
		const script = document.createElement('script')
		script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&language=${encodeURIComponent(language)}&loading=async&callback=__ynGoogleReady`
		script.async = true
		script.onerror = () => reject(new Error('Google Maps could not be loaded'))
		document.head.append(script)
	})

const fetchPlace = async ({ key, place, language }) => {
	await loadMaps(key, language)
	const { Place } = await window.google.maps.importLibrary('places')
	const result = new Place({ id: place, requestedLanguage: language })
	await result.fetchFields({ fields: ['reviews', 'rating', 'userRatingCount', 'googleMapsURI'] })
	// plain data only, so it can go into the cache
	return {
		url: result.googleMapsURI || '',
		reviews: (result.reviews || [])
			.filter((review) => review.rating >= 4 && review.text)
			.map((review) => ({
				rating: review.rating,
				text: String(review.text),
				author: review.authorAttribution?.displayName || '',
				authorUrl: review.authorAttribution?.uri || '',
				time: review.relativePublishTimeDescription || '',
			})),
	}
}

const el = (tag, className, text) => {
	const node = document.createElement(tag)
	if (className) node.className = className
	if (text) node.textContent = text
	return node
}

// the Google "G" (attribution next to the stars)
const googleMark = () => {
	const template = document.createElement('template')
	template.innerHTML = '<svg class="voice-card__google" viewBox="0 0 48 48" aria-hidden="true" focusable="false"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.9 6.1C12.5 13.6 17.8 9.5 24 9.5z"/><path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.2 5.3-4.6 6.9l7.4 5.8c4.3-4 6.9-9.9 6.9-17.2z"/><path fill="#FBBC05" d="M10.5 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C1 16.6 0 20.2 0 24s1 7.4 2.7 10.7l7.8-6.1z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.8c-2.1 1.4-4.8 2.3-8.5 2.3-6.2 0-11.5-4.1-13.4-9.9l-7.9 6.1C6.6 42.6 14.6 48 24 48z"/></svg>'
	return template.content.firstChild
}

const buildCard = (review, index, url, texts) => {
	const item = el('li', 'voice-card voice-card--google')
	item.id = `google-review-${index + 1}`
	item.dataset.slide = ''
	item.dataset.slideTitle = review.author
	const figure = el('figure', 'voice-card__figure')

	const head = el('div', 'voice-card__logo')
	const stars = el('span', 'voice-card__stars', '★★★★★'.slice(0, review.rating) + '☆☆☆☆☆'.slice(0, 5 - review.rating))
	stars.setAttribute('role', 'img')
	stars.setAttribute('aria-label', texts.stars.replace('%s', review.rating))
	head.append(googleMark(), stars)

	const quote = el('blockquote', 'voice-card__quote')
	quote.append(el('p', null, `„${review.text}“`))

	const caption = el('figcaption', 'voice-card__caption')
	if (review.author) {
		const author = el(review.authorUrl ? 'a' : 'span', 'voice-card__author', review.author)
		if (review.authorUrl) {
			author.href = review.authorUrl
			author.target = '_blank'
			author.rel = 'noopener'
		}
		caption.append(author, ' · ')
	}
	caption.append([texts.source, review.time].filter(Boolean).join(' · '))
	if (url) {
		const more = el('a', 'voice-card__more', texts.more)
		more.href = url
		more.target = '_blank'
		more.rel = 'noopener'
		caption.append(el('br'), more)
	}

	figure.append(head, quote, caption)
	item.append(figure)
	return item
}

export async function initGoogleReviews() {
	const configNode = document.getElementById('yn-google')
	const sliders = document.querySelectorAll('[data-google-reviews]')
	if (!configNode || !sliders.length || !window.$_yn?.cache) return

	let config
	try {
		config = JSON.parse(configNode.textContent)
	} catch {
		return
	}
	if (!config.key || !config.place) return

	let data
	try {
		data = await window.$_yn.cache.fetch(`google-reviews-${config.place}-${config.language}`, () => fetchPlace(config), CACHE_TTL)
	} catch (error) {
		console.warn('[google-reviews]', error)
		return
	}
	if (!data?.reviews?.length) return

	sliders.forEach((slider) => {
		const track = slider.querySelector('[data-slider-track]')
		if (!track) return
		const limit = Number(slider.dataset.googleReviews) || 3
		const texts = {
			source: slider.dataset.googleSource || 'Google',
			stars: slider.dataset.googleStars || '%s / 5',
			more: slider.dataset.googleMore || 'Google',
		}
		data.reviews.slice(0, limit).forEach((review, i) => track.append(buildCard(review, i, data.url, texts)))
		slider.dispatchEvent(new CustomEvent('slider:refresh'))
	})
}
